import React, { useState, useEffect } from "react";
import { Fetchexecutequery, Fetchheaderdata, Fetchupdatequery } from '../../../services/process/BRS1/brsService7';
import { CreateData } from '../../../services/process/process_data/process_data';
import {
  FileText, Bell, CheckCircle, Clock, Calendar, Users, MapPin,
  Search, Filter, Eye, Edit3, Trash2, Copy, BarChart2, Globe,
  Upload, Plus, X, AlertCircle, Check, Send, ArrowRight, Building,
  Award, Archive, Zap, List, Flag, ChevronRight, ChevronLeft,
  ChevronDown, ChevronUp, MoreVertical, Save, AlertTriangle,
  ThumbsUp, ThumbsDown, MessageSquare, RotateCcw, UserPlus,
  Activity, Target, TrendingUp, Settings, Download, RefreshCw,
} from "lucide-react";

const FONT = "'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

// ── Re-export shared primitives (in a real project these come from a shared file)
const C = {
  primary: "#14253d", accent: "#2563eb", accent2: "#0f766e",
  warn: "#d97706", danger: "#dc2626", success: "#16a34a",
  purple: "#7c3aed", bg: "#eef3f8", card: "#ffffff",
  border: "#d7e0ea", text: "#0f172a", muted: "#64748b", light: "#e8eef5",
};

const AppShell = ({ children, style = {} }) => (
  <div
    style={{
      minHeight: "100vh",
      width: "100%",
      background:
        "radial-gradient(circle at top left, rgba(37,99,235,.08), transparent 30%), radial-gradient(circle at top right, rgba(15,118,110,.08), transparent 26%), linear-gradient(180deg, #f7fafc 0%, #eef4fa 100%)",
      color: C.text,
      fontFamily: FONT,
      ...style,
    }}
  >
    {children}
  </div>
);

const PageWrap = ({ children, wide = false, style = {} }) => (
  <div
    style={{
      width: "100%",
      maxWidth: wide ? 1520 : 1320,
      margin: "0 auto",
      padding: "24px 28px 32px",
      boxSizing: "border-box",
      ...style,
    }}
  >
    {children}
  </div>
);

// ── User credentials from sessionStorage (Production Mode) ──────────────────
const getUserCredentials = () => JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

/* MOCK CREDENTIALS - COMMENTED OUT FOR PRODUCTION
const MOCK_CREDENTIALS = {
  user_id: 101, user_name: "admin_user", tenants_id: 1,
  groups_id: 2, selectedentity: 10, role: "form_filler", display_name: "St. Xavier's School",
};
*/

const MOCK_APPLICATIONS = [
  { id: 1, event_name: "Global EdTech Summit 2025", organizer: "EduWorld Org", applied_on: "2025-08-12", application_status: "shortlisted", review_status: "completed", invitation_status: "not_invited", last_updated_on: "2025-08-20" },
  { id: 2, event_name: "STEM Innovation Expo", organizer: "TechLearn Asia", applied_on: "2025-08-18", application_status: "under_review", review_status: "in_review", invitation_status: "not_invited", last_updated_on: "2025-08-22" },
  { id: 3, event_name: "South Asia Education Fair", organizer: "EduFair India", applied_on: "2025-07-30", application_status: "invited", review_status: "completed", invitation_status: "invited", last_updated_on: "2025-08-25" },
  { id: 4, event_name: "Digital Learning Conference", organizer: "EdTech Alliance", applied_on: "2025-07-10", application_status: "rejected", review_status: "completed", invitation_status: "not_invited", last_updated_on: "2025-08-01" },
  { id: 5, event_name: "International School Leaders Forum", organizer: "Global Ed Leaders", applied_on: "2025-08-05", application_status: "applied", review_status: "pending", invitation_status: "not_invited", last_updated_on: "2025-08-05" },
];

const MOCK_INVITATIONS = [
  { id: 1, event_name: "South Asia Education Fair", organizer: "EduFair India", invitation_date: "2025-08-25", acceptance_deadline: "2025-09-10", event_date: "2025-10-30", invitation_status: "pending" },
  { id: 2, event_name: "Global EdTech Summit 2025", organizer: "EduWorld Org", invitation_date: "2025-08-28", acceptance_deadline: "2025-09-15", event_date: "2025-11-15", invitation_status: "pending" },
];

const statusColors = {
  applied: { bg: "#dbeafe", text: "#1d4ed8" }, under_review: { bg: "#fef3c7", text: "#92400e" },
  shortlisted: { bg: "#d1fae5", text: "#065f46" }, invited: { bg: "#ede9fe", text: "#5b21b6" },
  accepted: { bg: "#d1fae5", text: "#065f46" }, rejected: { bg: "#fee2e2", text: "#991b1b" },
  expired: { bg: "#f3f4f6", text: "#6b7280" }, draft: { bg: "#f3f4f6", text: "#6b7280" },
  published: { bg: "#dbeafe", text: "#1d4ed8" }, registration_open: { bg: "#d1fae5", text: "#065f46" },
  pending: { bg: "#fef3c7", text: "#92400e" }, not_invited: { bg: "#f3f4f6", text: "#6b7280" },
  completed: { bg: "#d1fae5", text: "#065f46" }, archived: { bg: "#f3f4f6", text: "#6b7280" },
  active: { bg: "#d1fae5", text: "#065f46" },
};

const Badge = ({ status, label }) => {
  const s = statusColors[status] || { bg: "#f3f4f6", text: "#6b7280" };
  return (
    <span style={{ background: s.bg, color: s.text, padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700, letterSpacing: .4, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {label || status?.replace(/_/g, " ")}
    </span>
  );
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: C.card, borderRadius: 18, border: `1px solid rgba(148,163,184,.22)`, boxShadow: "0 16px 40px rgba(15,23,42,.06)", ...style }}>{children}</div>
);

const SectionHeader = ({ title, sub, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
    <div>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: C.text, letterSpacing: -.2 }}>{title}</h2>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted, lineHeight: 1.45 }}>{sub}</p>}
    </div>
    {action}
  </div>
);

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <Card style={{ padding: "20px 22px", flex: 1, minWidth: 160 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 900, color: C.text, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{ background: color + "14", borderRadius: 14, padding: 12 }}>
        <Icon size={24} color={color} />
      </div>
    </div>
  </Card>
);

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const base = { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 10, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", border: "none", transition: "all .15s", opacity: disabled ? .6 : 1, ...style };
  const variants = {
    primary: { background: C.accent, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    secondary: { background: C.light, color: C.primary, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    danger: { background: C.danger, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    success: { background: C.success, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    ghost: { background: "transparent", color: C.accent, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    outline: { background: "transparent", color: C.primary, border: `1.5px solid ${C.border}`, padding: size === "sm" ? "5px 13px" : "9px 19px", fontSize: size === "sm" ? 12 : 14 },
    warn: { background: C.warn, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
  };
  return (
    <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {Icon && <Icon size={size === "sm" ? 13 : 15} />}
      {children}
    </button>
  );
};

const Input = ({ ...props }) => (
  <input {...props} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: "#fff", outline: "none", boxSizing: "border-box", ...props.style }} />
);

const Select = ({ children, ...props }) => (
  <select {...props} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: "#fff", outline: "none", boxSizing: "border-box", ...props.style }}>
    {children}
  </select>
);

const Field = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
      {label} {required && <span style={{ color: C.danger }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const BarChart = ({ data, color = C.accent }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ width: "100%", height: `${(d.value / max) * 64}px`, background: color, borderRadius: "4px 4px 0 0", minHeight: 4 }} />
          <span style={{ fontSize: 9, color: C.muted, textAlign: "center" }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const Countdown = ({ deadline }) => {
  const [diff, setDiff] = useState(0);
  useEffect(() => {
    const update = () => setDiff(Math.max(0, new Date(deadline) - Date.now()));
    update(); const t = setInterval(update, 1000); return () => clearInterval(t);
  }, [deadline]);
  const days = Math.floor(diff / 86400000);
  const hrs = Math.floor((diff % 86400000) / 3600000);
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[["D", days], ["H", hrs]].map(([u, v]) => (
        <div key={u} style={{ background: C.primary, color: "#fff", borderRadius: 6, padding: "4px 8px", textAlign: "center", minWidth: 36 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{String(v).padStart(2, "0")}</div>
          <div style={{ fontSize: 9, opacity: .7 }}>{u}</div>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ icon: Icon = FileText, title, sub, action }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", gap: 12, textAlign: "center" }}>
    <div style={{ background: C.light, borderRadius: 16, padding: 20 }}><Icon size={32} color={C.muted} /></div>
    <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</div>
    <div style={{ fontSize: 13, color: C.muted, maxWidth: 280 }}>{sub}</div>
    {action}
  </div>
);

// \u2500\u2500 Mock data - COMMENTED OUT FOR PRODUCTION \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n/*\nconst MOCK_APPLICATIONS = [...];\nconst MOCK_INVITATIONS = [...];\n\n// Using production services: Fetchexecutequery, Fetchheaderdata, Fetchupdatequery, CreateData\n*/ \nconst MOCK_APPLICATIONS = [];\nconst MOCK_INVITATIONS = [];

const FALLBACK_APPLICATIONS = [
  {
    id: 1,
    event_name: "Global EdTech Summit 2025",
    organizer: "EduWorld Org",
    applied_on: "2025-08-12",
    application_status: "shortlisted",
    review_status: "under_review",
    invitation_status: "pending",
    last_updated_on: "2025-08-18",
  },
  {
    id: 2,
    event_name: "STEM Innovation Expo",
    organizer: "Future Learning Network",
    applied_on: "2025-08-20",
    application_status: "applied",
    review_status: "pending",
    invitation_status: "not_invited",
    last_updated_on: "2025-08-20",
  },
];

const FALLBACK_INVITATIONS = [
  {
    id: 1,
    event_name: "Global EdTech Summit 2025",
    organizer: "EduWorld Org",
    event_date: "2025-11-15",
    invitation_date: "2025-08-25",
    acceptance_deadline: "2025-09-10",
    invitation_status: "pending",
  },
  {
    id: 2,
    event_name: "School Leadership Forum",
    organizer: "EduWorld Org",
    event_date: "2026-01-20",
    invitation_date: "2025-08-27",
    acceptance_deadline: "2025-09-15",
    invitation_status: "accepted",
  },
];

const CREATOR_EVENTS = [
  { id: 1, event_name: "Global EdTech Summit 2025", event_status: "registration_open", applications: 87, invitations: 0, confirmations: 0, event_date: "2025-11-15", creator_user_id: 101 },
  { id: 2, event_name: "STEM Innovation Expo", event_status: "draft", applications: 0, invitations: 0, confirmations: 0, event_date: "2025-12-05", creator_user_id: 101 },
  { id: 3, event_name: "School Leadership Forum", event_status: "invitations_released", applications: 142, invitations: 60, confirmations: 38, event_date: "2026-01-20", creator_user_id: 101 },
];

// ── Main Component ─────────────────────────────────────────────────────────────
function EventManagement() {
  const uc = { ...getUserCredentials(), role: "event_creator" };
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const events = CREATOR_EVENTS;

  // SELECT * FROM event_platform.events
  // WHERE creator_user_id = ${uc.user_id} AND creator_entity_id = ${uc.selectedentity} AND tenant_id = ${uc.tenants_id} AND is_active = 1

  const filtered = events.filter(ev => {
    return (
      (!search || ev.event_name.toLowerCase().includes(search.toLowerCase())) &&
      (!statusFilter || ev.event_status === statusFilter)
    );
  });

  const ActionMenu = ({ ev }) => {
    const [open, setOpen] = useState(false);
    return (
      <div style={{ position: "relative" }}>
        <button onClick={() => setOpen(p => !p)} style={{ background: C.light, border: "none", borderRadius: 6, padding: "5px 8px", cursor: "pointer" }}>
          <MoreVertical size={14} color={C.muted} />
        </button>
        {open && (
          <div style={{ position: "absolute", right: 0, top: "100%", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,.12)", zIndex: 100, minWidth: 160, padding: 6 }}>
            {[
              { label: "Edit Event", icon: Edit3 }, { label: "View Applicants", icon: Users },
              { label: "View Analytics", icon: BarChart2 }, { label: "Clone Event", icon: Copy },
              { label: "Archive", icon: Archive, danger: true },
            ].map(item => (
              <button key={item.label} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", borderRadius: 6, color: item.danger ? C.danger : C.text, fontSize: 13, fontWeight: 600, textAlign: "left" }}>
                <item.icon size={13} /> {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <AppShell>
      <PageWrap>
        <SectionHeader
          title="My Events"
          sub={`Creator: User #${uc.user_id} · Entity #${uc.selectedentity} — Isolated view`}
          action={<Btn variant="outline" size="sm" icon={Plus}>Create New Event</Btn>}
        />

        {/* Quick stats */}
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          <StatCard label="Total Events" value={CREATOR_EVENTS.length} icon={Calendar} color={C.accent} />
          <StatCard label="Active" value={CREATOR_EVENTS.filter(e => e.event_status === "registration_open").length} icon={Zap} color={C.success} />
          <StatCard label="Total Applications" value={CREATOR_EVENTS.reduce((a, e) => a + e.applications, 0)} icon={FileText} color={C.purple} />
          <StatCard label="Confirmed" value={CREATOR_EVENTS.reduce((a, e) => a + e.confirmations, 0)} icon={CheckCircle} color={C.accent2} />
        </div>

        {/* Filter */}
        <Card style={{ padding: "12px 16px", marginBottom: 20, display: "flex", gap: 12 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events..." style={{ paddingLeft: 32 }} />
          </div>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 200 }}>
            <option value="">All Statuses</option>
            {["draft","published","registration_open","invitations_released","completed"].map(s => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </Select>
        </Card>

        {/* Table */}
        <Card style={{ overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.light }}>
                {["Event Name", "Status", "Event Date", "Applications", "Invitations", "Confirmations", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev, i) => (
                <tr key={ev.id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "#fff" : C.bg + "88" }}>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{ev.event_name}</div>
                  </td>
                  <td style={{ padding: "14px 16px" }}><Badge status={ev.event_status} /></td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: C.muted }}>{ev.event_date}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 16, color: C.accent }}>{ev.applications}</span>
                      <Btn variant="ghost" size="sm" icon={Eye} style={{ padding: "2px 8px" }}></Btn>
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", fontWeight: 800, fontSize: 16, color: C.purple }}>{ev.invitations}</td>
                  <td style={{ padding: "14px 16px", fontWeight: 800, fontSize: 16, color: C.success }}>{ev.confirmations}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn variant="outline" size="sm" icon={Eye}>Review</Btn>
                      <ActionMenu ev={ev} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState icon={Calendar} title="No events found" sub="Create your first event to get started" action={<Btn variant="primary" icon={Plus}>Create Event</Btn>} />}
        </Card>
      </PageWrap>
    </AppShell>
  );
}


export default EventManagement;
